import type { CityConfig } from "../city-template/types";

export const lasVegasConfig: CityConfig = {
  city: "Las Vegas",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "las-vegas",
  route: "/cities/las-vegas",
  title: "Emergency Passport Las Vegas NV | Expedited Passport Renewal | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Las Vegas, NV — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Clark County and Southern Nevada travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Las Vegas, NV",
  heroSubtitle: "Passport expired before your international trip? Need emergency passport renewal fast in Las Vegas? We help Clark County travelers get expedited passports in as little as 24 hours — no agency visit required. A+ BBB rated registered courier.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Las%20Vegas%20Nevada%20iconic%20Strip%20skyline%20at%20dusk%20with%20colorful%20neon%20hotel%20signs%20and%20casino%20towers%20illuminated%20against%20deep%20purple%20twilight%20sky%2C%20dramatic%20wide%20angle%20panoramic%20view%20of%20the%20world%20famous%20entertainment%20district%2C%20warm%20golden%20and%20magenta%20tones%20in%20the%20sky%2C%20no%20visible%20people%20or%20text%2C%20vibrant%20and%20cinematic%20cityscape%20photography%20highlighting%20the%20energy%20and%20excitement%20of%20the%20Nevada%20desert%20resort%20city&width=1800&height=700&seq=las-vegas-hero-001&orientation=landscape",
  heroImageAlt: "Las Vegas Nevada Strip skyline at dusk with neon casino towers and vibrant night lights",
  geoPosition: "36.1699;-115.1398",
  geoPlacename: "Las Vegas, Nevada",
  lastModified: "2026-06-02",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown Los Angeles — approximately 270 miles from Las Vegas via I-15",
  },
  nearbyCitiesZips: [
    { name: "Downtown Las Vegas", zips: "89101, 89102, 89104" },
    { name: "Henderson", zips: "89002, 89014, 89015" },
    { name: "Summerlin", zips: "89135, 89138, 89144" },
    { name: "North Las Vegas", zips: "89030, 89031, 89032" },
    { name: "Sunrise Manor", zips: "89110, 89121" },
    { name: "Enterprise", zips: "89141, 89148, 89178" },
    { name: "Spring Valley", zips: "89103, 89117, 89146" },
    { name: "Paradise", zips: "89109, 89119, 89120" },
    { name: "Boulder City", zips: "89005, 89006" },
    { name: "Mesquite", zips: "89027, 89034" },
    { name: "Pahrump", zips: "89041, 89048" },
    { name: "Jean / Primm", zips: "89019" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Las Vegas departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — ship from any UPS in Las Vegas" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Henderson NV", route: "/cities/henderson-nv" },
    { name: "Fast passport renewal in Reno", route: "/cities/reno" },
    { name: "Expedited passport help in Sparks", route: "/cities/sparks" },
    { name: "Urgent passport services in Los Angeles", route: "/cities/los-angeles" },
    { name: "Same-day passport help in San Diego", route: "/cities/san-diego" },
    { name: "Passport courier services in Phoenix area", route: "/states/arizona" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Las Vegas remotely via secure courier. We do not have a walk-in office in Las Vegas — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Las Vegas travelers.",
  fpcOfficeAddress: "300 N Los Angeles St, Los Angeles, CA 90012",
  fpcOfficeDistance: "Serving all of Clark County and Southern Nevada",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the entire Las Vegas metro area. From the famous Strip to Summerlin, Henderson, North Las Vegas, and the surrounding Clark County communities — we help Las Vegas travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Las Vegas neighborhoods and nearby Southern Nevada cities including Henderson, North Las Vegas, Summerlin, Spring Valley, Paradise, Enterprise, Boulder City, Mesquite, and Pahrump. If you are in Clark County or nearby Nye County, we have you covered.",
  howItWorksIntro: "Most Las Vegas travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait at a regional passport agency. Our emergency and expedited passport process is designed for Southern Nevada travelers who need fast, reliable results.",
  mistakesIntro: "With Harry Reid International Airport serving millions of international passengers, Las Vegas travelers cannot afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Las Vegas travelers trust us when their international departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Las Vegas? Here are the most common ones we hear from Clark County travelers.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  dmvWebsite: "https://dmv.nv.gov",
  dmvLabel: "Nevada DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Nearest agency is Los Angeles (270 miles)",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Las Vegas travelers with immediate departure needs — as fast as 24 hours for urgent international flights from Harry Reid International.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support across Clark County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Las Vegas travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Derek M.",
      location: "Summerlin, NV",
      rating: 5,
      text: "My flight to Tokyo was in 3 days and I realized my passport expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved my international trip from Harry Reid.",
      service: "Emergency Passport",
    },
    {
      name: "Priya S.",
      location: "Henderson, NV",
      rating: 5,
      text: "Needed passport renewal for a Cancun vacation. The process was seamless, they reviewed all my documents, and I had my passport in 4 days. Would 100% use again.",
      service: "Passport Renewal",
    },
    {
      name: "James R.",
      location: "Las Vegas, NV",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Italy. They walked me through the DS-64 process and got me a replacement in 2 days. Unbelievable turnaround.",
      service: "Lost Passport",
    },
    {
      name: "Karen T.",
      location: "North Las Vegas, NV",
      rating: 5,
      text: "As a frequent traveler for entertainment industry work, I can't afford delays. Fast Passport Center is my go-to every time. They're professional, fast, and always reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "lv-1",
      name: "Las Vegas Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1001 E Sunset Rd",
      city: "Las Vegas",
      state: "NV",
      zip: "89119",
      phone: "(702) 361-9280",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Near Harry Reid International Airport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility near the airport. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "lv-2",
      name: "North Las Vegas Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2900 Losee Rd",
      city: "North Las Vegas",
      state: "NV",
      zip: "89030",
      phone: "(702) 399-8188",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from North Las Vegas downtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves North Las Vegas and Sunrise Manor. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "lv-3",
      name: "Henderson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 Water St",
      city: "Henderson",
      state: "NV",
      zip: "89015",
      phone: "(702) 564-9901",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from Henderson downtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Henderson and Boulder City area. Call ahead to confirm availability.",
      icon: "ri-mail-line",
    },
    {
      id: "lv-4",
      name: "Clark County Government Center",
      type: "County Clerk Passport Acceptance",
      address: "500 S Grand Central Pkwy",
      city: "Las Vegas",
      state: "NV",
      zip: "89155",
      phone: "(702) 455-4336",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "0.5 miles from downtown Las Vegas",
      website: "https://www.clarkcountynv.gov/",
      websiteLabel: "Clark County Clerk",
      notes: "Full-service government center with extended hours. Accepts walk-ins but appointments recommended.",
      icon: "ri-government-line",
    },
    {
      id: "lv-5",
      name: "Summerlin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10100 W Charleston Blvd",
      city: "Las Vegas",
      state: "NV",
      zip: "89135",
      phone: "(702) 228-8800",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "In the Summerlin community center area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for west Las Vegas and Summerlin residents. Appointments strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "lv-6",
      name: "Nevada DMV — Las Vegas",
      type: "State DMV Passport Acceptance",
      address: "8250 W Flamingo Rd",
      city: "Las Vegas",
      state: "NV",
      zip: "89147",
      phone: "(702) 486-4368",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "West Las Vegas near Spring Valley",
      website: "https://dmv.nv.gov/",
      websiteLabel: "Nevada DMV",
      notes: "Select Nevada DMV locations offer passport acceptance. Call ahead to confirm service availability.",
      icon: "ri-car-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 270 miles to Los Angeles", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Las Vegas photo shops often use generic settings. Passport photos must be exactly 2×2 inches with a white or off-white background — casino photo booths are not acceptable.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Las Vegas acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Las Vegas applicants show up with incorrect amounts and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Los Angeles regional agency will turn you away for emergency appointments. Don't make the 270-mile drive without documentation.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Las Vegas families at the acceptance clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Nevada has no local passport agency — standard processing takes 8–11 weeks and the nearest agency is in Los Angeles. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Las Vegas or Henderson." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Las Vegas address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Las Vegas?",
      a: "Fast Passport Center offers emergency passport processing in Las Vegas in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. Since Nevada has no local passport agency, this courier service is the fastest reliable option for Las Vegas travelers.",
    },
    {
      q: "Does Nevada have a local passport agency near Las Vegas?",
      a: "No. Nevada does not have a regional passport agency. The nearest agency is the Los Angeles Passport Agency, approximately 270 miles away via I-15. Most Las Vegas travelers find it far more practical to use Fast Passport Center's courier service — our 24–48 hour emergency processing means you never have to make the drive.",
    },
    {
      q: "Where can I get a fast passport renewal in Las Vegas?",
      a: "Fast Passport Center offers fast passport renewal in Las Vegas with no office visit required. We serve all Las Vegas neighborhoods including Summerlin, Henderson, North Las Vegas, Spring Valley, and Paradise. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "Can I get a same-day passport in Las Vegas?",
      a: "Same-day passports are only available at regional passport agencies — and the nearest one to Las Vegas is in Los Angeles, 270 miles away. Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Las Vegas and Clark County residents without a long-distance drive.",
    },
    {
      q: "How fast can I get a passport for a flight out of Harry Reid International Airport?",
      a: "If you have an upcoming international flight from Harry Reid International Airport (LAS) and need a passport fast, Fast Passport Center can process your passport in as little as 24–48 hours. Our emergency courier service is specifically designed for travelers with imminent departures who need fast, guaranteed turnaround.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Las Vegas?",
      a: "Popular Las Vegas acceptance facilities include the Las Vegas Main Post Office (89119), North Las Vegas Post Office (89030), Henderson Post Office (89015), Clark County Government Center (89155), and the Summerlin Post Office (89135). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I need a child's passport in Las Vegas?",
      a: "Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Las Vegas families through the entire process and help schedule acceptance clerk appointments at the most convenient Clark County facility.",
    },
    {
      q: "What documents do I need for a first-time passport in Las Vegas?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (Nevada driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "How much does expedited passport service cost in Las Vegas?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline and departure date.",
    },
    {
      q: "Can you help with a lost passport in Las Vegas?",
      a: "Yes. If you lost your passport in Las Vegas — especially common after a hotel stay or entertainment event — Fast Passport Center provides lost passport replacement services with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours so you don't miss your trip.",
    },
    {
      q: "Do you help with passport photos in Las Vegas?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, FedEx Office, and UPS Store locations in Las Vegas offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What if I live in Henderson, Summerlin, or North Las Vegas?",
      a: "We serve the entire Las Vegas metro including Henderson, North Las Vegas, Summerlin, Spring Valley, Paradise, Enterprise, and Boulder City. Our overnight shipping labels work from any UPS location in Clark County, so your specific neighborhood doesn't affect processing speed or turnaround time.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Las Vegas Strip", address: "3758 Las Vegas Blvd S, Las Vegas, NV 89109", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Summerlin", address: "10120 W Charleston Blvd, Las Vegas, NV 89135", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Downtown Las Vegas", address: "301 N Main St, Las Vegas, NV 89101", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Henderson", address: "101 N Stephanie St, Henderson, NV 89014", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — North Las Vegas", address: "1807 W Craig Rd, North Las Vegas, NV 89032", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Henderson", address: "590 College Dr, Henderson, NV 89015", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Summerlin", address: "8880 W Charleston Blvd, Las Vegas, NV 89117", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Spring Valley", address: "5051 S Fort Apache Rd, Las Vegas, NV 89148", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
  ],
  dmvLocations: [
    {
      name: "Nevada DMV — West Flamingo",
      address: "8250 W Flamingo Rd, Las Vegas, NV 89147",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and Nevada driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "Nevada DMV — Henderson",
      address: "2250 N Green Valley Pkwy, Henderson, NV 89014",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service Henderson DMV. Serves Green Valley, Anthem, and East Henderson.",
      featured: false,
    },
    {
      name: "Nevada DMV — North Las Vegas",
      address: "2501 E Cheyenne Ave, North Las Vegas, NV 89030",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Serves North Las Vegas and Sunrise Manor. Online appointment booking available.",
      featured: true,
    },
    {
      name: "Nevada DMV — Summerlin",
      address: "9530 Del Webb Blvd, Las Vegas, NV 89134",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Convenient for Summerlin and western Las Vegas residents. REAL ID processing.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "LAS",
      name: "Harry Reid International Airport",
      distance: "5 miles from the Las Vegas Strip",
      address: "5757 Wayne Newton Blvd, Las Vegas, NV 89119",
      note: "The primary international airport for Las Vegas. Major hub for international flights to Mexico, Canada, Europe, and Asia. Over 50 million passengers annually.",
      flightUrl: "https://www.harryreidairport.com/",
      flightLabel: "Harry Reid Airport",
      featured: true,
    },
    {
      code: "BOU",
      name: "Boulder City Municipal Airport",
      distance: "25 miles from downtown Las Vegas",
      address: "1 Airport Rd, Boulder City, NV 89005",
      note: "Small general aviation airport serving Boulder City. Grand Canyon tour flights depart from here.",
      flightUrl: "https://www.bcity.com/airport",
      flightLabel: "Boulder City Airport",
      featured: false,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "270 miles from Las Vegas via I-15",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Alternative hub for extended international routes. Also home to the nearest Los Angeles Regional Passport Agency for emergency appointments.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
  ],
};